/**
 * The information tower (WORKBOARD I5, §12): humble — two person-heights to its eye, no height
 * upgrades (ruling 2); its advantage is where it is built. It sees radar dots (ruling 26), so the
 * head is a rotating radar panel, not optics. Person-scale details (a ladder, a cabinet hatch)
 * say how big it is (§10.2 rule 2); a concrete pad with a skirt below ground keeps it seated on
 * a slope (rule 5). One hue in three values: the frame is metal, the cabinet and head the team.
 *
 * Built in person units (a person is 1.8 tall) and scaled by `personHeight / 1.8`. `eye` sits at
 * exactly TOWER_EYE_PERSONS person-heights — the point lane A's tower observer looks from.
 *
 * Design reference: design_sheets/recon.html
 */
import * as THREE from 'three';
import { REFERENCE } from '../constants';
import { bakeStaticParts, markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';
import type { PersonTier } from './personModel';

/** The eye is this many person-heights above the ground (ruling 2). */
export const TOWER_EYE_PERSONS = 2;

export interface TowerModel {
  root: THREE.Group;
  full: THREE.Group;
  silhouette: THREE.Group;
  head: THREE.Group;      // rotates about y (the radar sweep)
  eye: THREE.Object3D;    // the observer's eye, TOWER_EYE_PERSONS × 1.8 person units up
  tier: PersonTier;
}

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
/** A thin beam from a to b. */
function beam(parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, t: number, color: string): void {
  const m = new THREE.Mesh(new THREE.BoxGeometry(t, a.distanceTo(b), t), mat(color));
  m.position.copy(a).add(b).multiplyScalar(0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
  parent.add(m);
}

const CONCRETE = '#9a978e';
const HOLE = '#1c1c20';

/**
 * 63.4's future, partial destruction (ARCHITECTURE §5.13: "a tower: whole, damaged, ruined"): the tower's forms between
 * whole and wrecked. The head is its own group (`head`: the function part — the tower sees through it) and the frame
 * stands until the wreck:
 * - `damaged`: the radar panel torn — its left third gone, a hole, a shard at the tear — so the head is narrower; a deck
 *   rail and a brace gone;
 * - `headless`: the head knocked off the deck and lying at the tower's foot (`fallen:head`: a detachable piece); a
 *   snapped post on the deck; two rails gone. The tower is blind; its frame still stands.
 */
export type TowerForm = 'whole' | 'damaged' | 'headless';
export const TOWER_FORMS: TowerForm[] = ['whole', 'damaged', 'headless'];

export function buildTowerModel(p: UnitPalette, personHeight: number, skirtDepth = 0.6, opts: { form?: TowerForm; fallen?: boolean } = {}): TowerModel {
  const form = opts.form ?? 'whole';
  const root = new THREE.Group();
  root.scale.setScalar(personHeight / 1.8);
  const full = new THREE.Group(); root.add(full);
  const EYE = TOWER_EYE_PERSONS * 1.8;          // 3.6
  const DECK = EYE - 0.75;                       // the head sits on a small deck

  // Pad and skirt (below ground)
  box(full, 1.9, 0.14, 1.9, CONCRETE, 0, 0.07, 0);
  box(full, 1.95, skirtDepth, 1.95, CONCRETE, 0, -skirtDepth / 2, 0);

  // Four splayed legs, two rings of braces
  const footR = 0.75, topR = 0.32;
  const corners = [[1, 1], [1, -1], [-1, -1], [-1, 1]];
  const at = (r: number, y: number, c: number[]) => new THREE.Vector3(c[0] * r, y, c[1] * r);
  for (const c of corners) beam(full, at(footR, 0.14, c), at(topR, DECK, c), 0.09, p.metal);
  for (const y of [1.0, 2.0]) {
    const r = footR + (topR - footR) * ((y - 0.14) / (DECK - 0.14));
    for (let i = 0; i < 4; i++) beam(full, at(r, y, corners[i]), at(r, y, corners[(i + 1) % 4]), 0.05, p.metal);
  }
  // diagonal braces on the two faces a viewer sees most (the damaged forms lose the second)
  beam(full, at(footR, 0.14, corners[0]), at(0.55, 2.0 - 0.95, corners[1]), 0.04, p.metal);
  if (form === 'whole') beam(full, at(footR, 0.14, corners[2]), at(0.55, 2.0 - 0.95, corners[3]), 0.04, p.metal);
  else beam(full, at(footR, 0.14, corners[2]), at(0.66, 0.6, corners[3]), 0.04, p.metal);   // snapped a third of the way up

  // Ladder up the front (+Z) face: two rails and rungs a person climbs
  const ladderZ = 0.62;
  // 63.2: the reference ladder — rails REFERENCE.ladder.width apart (were 0.34: a person's shoulders didn't fit), rungs its spacing
  const half = REFERENCE.ladder.width / 2;
  for (const x of [-half, half]) beam(full, new THREE.Vector3(x, 0.14, ladderZ + 0.1), new THREE.Vector3(x, DECK, topR + 0.06), 0.035, p.metal);
  for (let y = 0.4; y < DECK - 0.1; y += REFERENCE.ladder.rung) {
    const z = ladderZ + 0.1 + (topR + 0.06 - ladderZ - 0.1) * ((y - 0.14) / (DECK - 0.14));
    box(full, REFERENCE.ladder.width, 0.03, 0.03, p.metal, 0, y, z);
  }

  // Equipment cabinet at the base, in the team body, with a hatch (person scale)
  box(full, 0.6, 0.9, 0.42, p.body, 0, 0.59, -0.35);
  box(full, 0.4, 0.6, 0.02, p.dark, 0, 0.6, -0.57);      // hatch on the back face
  box(full, 0.62, 0.06, 0.44, p.dark, 0, 1.07, -0.35);   // cabinet lid

  // Deck and rail
  box(full, 0.95, 0.08, 0.95, p.dark, 0, DECK, 0);
  const rails: Array<[number, number, number, number]> = [[0, 0.46, 0.95, 0.04], [0, -0.46, 0.95, 0.04], [0.46, 0, 0.04, 0.95], [-0.46, 0, 0.04, 0.95]];
  rails.slice(0, form === 'whole' ? 4 : form === 'damaged' ? 3 : 2).forEach(([x, z, w, d]) => {
    box(full, w, 0.04, d, p.metal, x, DECK + 0.32, z);
    for (const e of [-1, 1]) box(full, 0.04, 0.28, 0.04, p.metal, x + (w > d ? e * (w / 2 - 0.02) : 0), DECK + 0.18, z + (w > d ? 0 : e * (d / 2 - 0.02)));   // its posts (63.4's forms: a rail left standing must not float)
  });

  // Head: a rotating radar panel on a short post, the team flash band, the eye at the top
  const head = new THREE.Group(); head.name = 'head'; head.position.y = DECK + 0.04; full.add(head);
  /** The panel and its edges, `from`…`to` across (x, of ±0.76): a torn panel keeps a part. */
  const radar = (g: THREE.Object3D, from: number, to: number) => {
    const w = to - from, c = (from + to) / 2;
    const panel = box(g, w * 1.5 / 1.52, 0.6, 0.08, p.body, c, 0.62, 0.12);   // the radar panel, tilted back: the tower's signature shape
    panel.rotation.x = -0.25;
    box(g, w, 0.06, 0.1, p.dark, c, 0.93, 0.05);                           // its top edge
    box(g, w, 0.06, 0.1, p.flash, c, 0.32, 0.17);                          // and a flash lip along the bottom
  };
  if (form === 'headless') {
    const stub = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 0.12, 8), mat(p.metal)); stub.position.y = 0.06; head.add(stub);   // the post, snapped
    // the head lying at the tower's foot, panel down on its face beside the pad (63.4 step 7: in play the sim lays it
    // where it fell, drawn as debris — `fallen: false` leaves it out here)
    if (opts.fallen !== false) {
      const fallen = new THREE.Group(); fallen.name = 'fallen:head'; fallen.position.set(1.6, 0.06, 1.0); fallen.rotation.order = 'YXZ'; fallen.rotation.set(-Math.PI / 2 + 0.2, 0.9, 0.1); full.add(fallen);
      box(fallen, 0.32, 0.12, 0.3, p.body, 0, 0.36 - 0.62, 0);
      box(fallen, 0.34, 0.05, 0.32, p.flash, 0, 0.44 - 0.62, 0);
      const lying = new THREE.Group(); lying.position.y = -0.62; fallen.add(lying);
      radar(lying, -0.76, 0.76);
    }
  } else {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 0.3, 8), mat(p.metal)); post.position.y = 0.15; head.add(post);
    box(head, 0.32, 0.12, 0.3, p.body, 0, 0.36, 0);                       // drive housing
    box(head, 0.34, 0.05, 0.32, p.flash, 0, 0.44, 0);                     // the flash band
    if (form === 'whole') radar(head, -0.76, 0.76);
    else {
      radar(head, -0.24, 0.76);                                           // its left third torn away
      const shard = box(head, 0.16, 0.42, 0.06, p.body, -0.28, 0.58, 0.14); shard.rotation.set(-0.25, 0, 0.35);   // a shard at the tear
      box(head, 0.16, 0.13, 0.1, HOLE, 0.3, 0.66, 0.13).rotation.x = -0.25;   // a hole through it
    }
  }
  const eye = new THREE.Object3D(); eye.position.y = EYE; full.add(eye);

  // Far tier: pad, a tapered block for the frame, the head
  const silhouette = new THREE.Group(); root.add(silhouette);
  box(silhouette, 1.9, 0.2, 1.9, CONCRETE, 0, 0.1, 0);
  const frame = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.8, DECK, 4), mat(p.metal));
  frame.position.y = DECK / 2; frame.rotation.y = Math.PI / 4; silhouette.add(frame);
  if (form === 'whole') box(silhouette, 1.5, 0.7, 0.3, p.body, 0, DECK + 0.6, 0);
  else if (form === 'damaged') box(silhouette, 1.0, 0.7, 0.3, p.body, 0.26, DECK + 0.6, 0);   // its far tier follows the form: a torn head, or none
  silhouette.visible = false;

  return { root, full, silhouette, head, eye, tier: 'full' };
}

/**
 * 63.4 step 7: the radar head as a piece on its own (person units, unscaled): the drive housing, the flash band and the
 * panel, as it stood on the post — the long axis x (the panel's width), the panel's face toward +z. The debris view lays
 * it where the sim put it, face down.
 */
export function buildHeadPiece(p: UnitPalette): THREE.Group {
  const g = new THREE.Group();
  box(g, 0.32, 0.12, 0.3, p.body, 0, 0.36, 0);
  box(g, 0.34, 0.05, 0.32, p.flash, 0, 0.44, 0);
  const panel = box(g, 1.5, 0.6, 0.08, p.body, 0, 0.62, 0.12); panel.rotation.x = -0.25;
  box(g, 1.52, 0.06, 0.1, p.dark, 0, 0.93, 0.05);
  box(g, 1.52, 0.06, 0.1, p.flash, 0, 0.32, 0.17);
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.18, 8), mat(p.metal)); post.position.y = 0.21; g.add(post);   // the snapped end of the post
  return g;
}

/** Sweep the radar head: one turn every `period` seconds. */
export function updateTowerModel(m: TowerModel, dt: number, period = 3): void {
  m.head.rotation.y += (Math.PI * 2 * dt) / period;
}

export function bakeTowerModel(m: TowerModel): number {
  let n = bakeStaticParts(m.full, { exclude: [m.head] }).partsBaked;
  n += bakeStaticParts(m.head).partsBaked;
  m.silhouette.visible = true;
  n += bakeStaticParts(m.silhouette).partsBaked;
  setTowerTier(m, m.tier);
  return n;
}

export function setTowerTier(m: TowerModel, tier: PersonTier): void {
  m.tier = tier;
  m.full.visible = tier === 'full';
  m.silhouette.visible = tier === 'silhouette';
}
